import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Search, Check, ChevronDown } from 'lucide-react';
import { VEHICLE_TYPES, VEHICLE_CATEGORIES, getVehicleTypeLabel, getVehicleTypeIcon } from '../constants/vehicleTypes';

/**
 * VehicleTypeSelector - Sélecteur de type de véhicule avec recherche
 * @param {string} value - Valeur sélectionnée
 * @param {function} onChange - Callback quand la valeur change
 * @param {string} placeholder - Placeholder du bouton
 * @param {boolean} disabled - Désactiver le sélecteur
 */
const VehicleTypeSelector = ({ 
  value, 
  onChange, 
  placeholder = "Sélectionner un type",
  disabled = false 
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filtrer les types de véhicules par recherche
  const filteredTypes = useMemo(() => {
    if (!searchQuery.trim()) {
      return VEHICLE_TYPES;
    }
    
    const query = searchQuery.toLowerCase().trim();
    return VEHICLE_TYPES.filter(type => 
      type.label.toLowerCase().includes(query) ||
      type.value.toLowerCase().includes(query) ||
      type.category.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Grouper par catégorie
  const groupedTypes = useMemo(() => {
    const groups = {};
    filteredTypes.forEach(type => {
      if (!groups[type.category]) {
        groups[type.category] = [];
      }
      groups[type.category].push(type);
    });
    return groups;
  }, [filteredTypes]);

  // Trouver la catégorie label
  const getCategoryLabel = (categoryValue) => {
    const category = VEHICLE_CATEGORIES.find(c => c.value === categoryValue);
    return category?.label || categoryValue;
  };

  const handleSelect = (typeValue) => {
    onChange(typeValue);
    setOpen(false);
    setSearchQuery('');
  };

  const selectedType = VEHICLE_TYPES.find(t => t.value === value);

  return (
    <>
      {/* Bouton de sélection */}
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        disabled={disabled}
        className="w-full justify-between font-normal"
        onClick={() => setOpen(true)}
      >
        {selectedType ? (
          <span className="flex items-center gap-2">
            <span className="text-lg">{selectedType.icon}</span>
            <span>{selectedType.label}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {/* Modal de sélection */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md p-0">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>Sélectionner un type de véhicule</DialogTitle>
          </DialogHeader>
          
          {/* Barre de recherche */}
          <div className="px-4 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
          </div>

          {/* Liste des types */}
          <ScrollArea className="h-[350px] px-2">
            {Object.keys(groupedTypes).length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <p>Aucun type trouvé pour "{searchQuery}"</p>
              </div>
            ) : (
              <div className="space-y-2 pb-4">
                {Object.entries(groupedTypes).map(([category, types]) => (
                  <div key={category}>
                    {/* En-tête de catégorie */}
                    <div className="sticky top-0 bg-background px-2 py-1.5 text-xs font-semibold text-muted-foreground border-b">
                      {getCategoryLabel(category)}
                    </div>
                    
                    {/* Types dans cette catégorie */}
                    <div className="py-1">
                      {types.map((type) => (
                        <button
                          key={type.value}
                          onClick={() => handleSelect(type.value)}
                          className={`
                            w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left
                            transition-colors hover:bg-accent
                            ${value === type.value ? 'bg-accent' : ''}
                          `}
                        >
                          <span className="text-xl flex-shrink-0">{type.icon}</span>
                          <span className="flex-1 font-medium">{type.label}</span>
                          {value === type.value && (
                            <Check className="h-4 w-4 text-primary flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VehicleTypeSelector;
